import clsx from 'clsx';
import { Property } from 'csstype';
import {
  type ChangeEvent,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import { useIntl } from 'react-intl';

import { ResizeIcon } from '@dynatrace/strato-icons';

import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import { useFocusRing } from '../../core/hooks/useFocusRing.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import type { KeyboardEvents } from '../../core/types/events.js';
import type { FormControlProps } from '../../core/types/form-control-props.js';
import { MaskingProps } from '../../core/types/masking-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { mergeProps as _mergeProps } from '../../core/utils/merge-props.js';
import { Flex } from '../../layouts/flex/Flex.js';
import type { ValidateFieldOptions } from '../form/form.types.js';
import { useInternalFormRegistration } from '../form/hooks/useInternalFormRegistration.js';
import { InternalBaseItem as BaseItem } from '../forms-core/components/form-field-messages/FormFieldMessageBaseItem.js';
import { withFormFieldMessageContextProvider } from '../forms-core/components/form-field/FormFieldMessageContextProvider.js';
import { useFormControl } from '../forms-core/hooks/useFormControl.js';
import {
  getI18nValidationState,
  useFormFieldValidation,
} from '../forms-core/hooks/useFormFieldValidation.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import { useContainerFocus } from '../hooks/useContainerFocus.js';
import type { FormControlRef } from '../shared-types.js';
import type { MessageTarget } from '../validation/message-routing.js';
import type { ValidationState } from '../validation/types.js';
import { useCustomMessageEmitter } from '../validation/useCustomMessageEmitter.js';
import { useValidationStateEmitter } from '../validation/useValidationStateEmitter.js';
import * as styles from './TextArea.sty.js';

const COMPONENT_NAME = 'TextArea';

// #region Typings
/**
 * @public
 */
export type TextAreaProps = FormControlProps<
  string,
  (value: string, event: ChangeEvent<HTMLTextAreaElement>) => void | never,
  HTMLDivElement
> &
  StylingProps &
  DataTestId &
  MaskingProps &
  KeyboardEvents &
  BehaviorTrackingProps & {
    /**
     * Internal: associates this control with a `_Form` rendered outside its
     * React subtree via the native `form` attribute. Unstable.
     * @internal
     */
    _form?: string;
    /**
     * Internal: routes this control's native/Strato validation messages to the
     * field outlet (default), the enclosing form's outlet, or both. Unstable.
     * @internal
     */
    _messageTarget?: MessageTarget | MessageTarget[];
    /**
     * Handler that is called when the element's focus status changes.
     * @deprecated Use onFocus/onBlur instead.
     */
    onFocusChange?: (isFocused: boolean) => void;
    placeholder?: string;
    /**
     * This attribute indicates whether the textarea is resizable by the user, and, if so, in which direction.
     * @defaultValue 'both'
     */
    resize?: 'both' | 'vertical' | 'horizontal' | 'none';
    /**
     * The visible width of the text control, in average character widths.
     * If it is specified, it must be a positive integer.
     * @defaultValue 20
     */
    cols?: number;
    /**
     * The number of visible text lines for the control. If it is specified, it must be a positive integer.
     * @defaultValue 2
     */
    rows?: number;
    /**
     * The minimum number of characters required that the user should enter.
     * Doesn't stop the user from removing characters so that the number entered goes past the minimum,
     * but it does make the value entered into the <textarea> invalid.
     * With a set minlength an empty <textarea> is still considered valid unless you also have the required attribute set.
     */
    minLength?: number;
    /**
     * The maximum number of characters that the user can enter. If this value isn't specified,
     * the user can enter an unlimited number of characters.
     */
    maxLength?: number;
    /**
     * This attribute indicates whether the value of the control can be automatically completed by the browser.
     * If no autocompletion is desired, it can be set to "off".
     */
    autoComplete?: string;
    /**
     * Sets the width of the textarea element.
     * @defaultValue 'full'
     */
    width?: 'content' | 'full' | Property.Width<string & Record<string, never>>;
  };

/**
 * @public
 */
export type TextAreaInputRef = Omit<
  FormControlRef<HTMLDivElement>,
  'inputRef'
> & {
  readonly inputRef?: HTMLTextAreaElement | null;
};
// #endregion

const TextAreaRoot = /* @__PURE__ */ forwardRef<
  TextAreaInputRef,
  TextAreaProps
>((props, forwardedRef) => {
  const intl = useIntl();

  const defaultPlaceholder = intl.formatMessage({
    defaultMessage: 'Add text',
    id: 'E7kKT3Td1FKB37wr',
    description: 'Default placeholder for the text area input.',
  });

  const {
    onChange,
    onFocus,
    onBlur,
    onValidityChange,
    minLength,
    maxLength,
    id: propId,
    name,
    _form,
    _messageTarget,
    disabled: propDisabled,
    required: propRequired,
    readOnly: propReadOnly,
    resize = 'both',
    // oxlint-disable-next-line typescript/no-deprecated -- APPDEV-18321
    controlState,
    value: propValue,
    defaultValue,
    width = 'full',
    placeholder = defaultPlaceholder,
    'aria-describedby': ariaDescribedby,
    'aria-disabled': ariaDisabledProp,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    className: consumerClassName,
    style: consumerStyle,
    ...remainingProps
  }: TextAreaProps = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);

  const {
    id,
    disabled,
    required,
    ariaDisabled,
    readOnly,
    inputProps,
    hintProps,
    hint,
    showError: hasError,
    messageDispatch: messagesContextDispatch,
  } = useFormControl({
    propId,
    propDisabled,
    propRequired,
    propAriaDisabled: ariaDisabledProp,
    propReadOnly,
    controlState,
  });
  const disabledOrAriaDisabled = disabled || ariaDisabled || false;

  // Imperative Handle Construction
  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const formValidityChangeRef = useRef<
    ((state: ValidationState) => void) | undefined
  >(undefined);
  const _emitValidationState = useValidationStateEmitter(
    controlState
      ? undefined
      : (state) => formValidityChangeRef.current?.(state),
  );
  const emitValidationState = useCustomMessageEmitter(
    _emitValidationState,
    () => getI18nValidationState(intl, textareaRef.current, 'manual'),
  );

  useImperativeHandle(forwardedRef, (): TextAreaInputRef => {
    return {
      element: containerRef.current,
      inputRef: textareaRef.current,
      focus() {
        textareaRef.current?.focus();
      },
      validate() {
        textareaRef.current?.checkValidity();
        messagesContextDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
        emitValidationState(
          getI18nValidationState(intl, textareaRef.current, 'manual'),
        );
      },
      // DEPRECATION: [APPDEV-18178] BREAKING-CHANGE: Remove validateAsync() and change validate() to return Promise<ValidationState>.
      validateAsync: async () => {
        textareaRef.current?.checkValidity();
        messagesContextDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
        return emitValidationState(
          getI18nValidationState(intl, textareaRef.current, 'manual'),
        );
      },
    };
  });

  const [value, setValue] = useControllableState({
    defaultValue,
    value: propValue,
  });

  const {
    onChange: validationOnChange,
    onInvalid: validationOnInvalid,
    ariaProps,
  } = useFormFieldValidation(
    textareaRef,
    { required, minLength, maxLength, value: value ?? '' },
    undefined,
    undefined,
    _emitValidationState,
    controlState,
  );

  const validateFieldForForm = ({
    reveal,
    trigger,
  }: ValidateFieldOptions): ValidationState => {
    textareaRef.current?.checkValidity();
    if (reveal) {
      messagesContextDispatch?.({
        type: FormFieldMessagesAction.SETISTOUCHED,
        payload: { isTouched: true },
      });
    }
    return emitValidationState(
      getI18nValidationState(intl, textareaRef.current, trigger),
    );
  };

  const formControl = useInternalFormRegistration({
    id,
    name,
    form: _form,
    messageTarget: _messageTarget,
    onValidityChange,
    getValue: () => value ?? '',
    focus: () => textareaRef.current?.focus({ focusVisible: true }),
    validateField: (options) => Promise.resolve(validateFieldForForm(options)),
    resetValidation: () => {
      textareaRef.current?.setCustomValidity('');
      messagesContextDispatch?.({
        type: FormFieldMessagesAction.SETISTOUCHED,
        payload: { isTouched: false, resetShowError: true },
      });
    },
  });
  formValidityChangeRef.current = formControl.onValidityChange;

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target?.value;
    setValue(newValue);
    onChange?.(newValue, event);
    validationOnChange();
  };

  const mergedAriaDescribedby =
    [ariaDescribedby, inputProps['aria-describedby']]
      .filter(Boolean)
      .join(' ') || undefined;

  const { containerClassName, ...containerProps } =
    useContainerFocus<HTMLDivElement>({
      onFocus,
      onBlur,
      disabled,
      emitValidationState,
    });

  const {
    focusProps: { onFocus: focusRingOnFocus, onBlur: focusRingOnBlur },
    focusClassName,
  } = useFocusRing({
    color: hasError ? 'critical' : 'neutral',
    ignoreModality: true,
    disabled,
  });

  const styleVariants = {
    error: hasError,
    disabled: disabledOrAriaDisabled,
    readOnly,
  };

  return (
    <Flex
      gap={4}
      flexDirection="column"
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      ref={containerRef}
      className={clsx(containerClassName, consumerClassName)}
      style={consumerStyle}
      {...containerProps}
      {...behavioralTrackingProps}
      data-dt-component={COMPONENT_NAME}
    >
      <div
        className={styles.container({
          contentWidth: width === 'content',
          resize,
        })}
        style={{ width }}
      >
        <textarea
          ref={textareaRef}
          id={id}
          name={name}
          form={_form}
          minLength={minLength}
          maxLength={maxLength}
          className={clsx(styles.textArea(styleVariants), focusClassName)}
          disabled={disabled}
          required={required}
          readOnly={readOnly}
          value={value}
          aria-required={required}
          aria-disabled={ariaDisabled}
          placeholder={placeholder}
          onChange={handleChange}
          onInvalid={validationOnInvalid}
          onFocus={focusRingOnFocus}
          onBlur={focusRingOnBlur}
          {..._mergeProps(
            restProps,
            controlState
              ? { ...inputProps, 'aria-describedby': mergedAriaDescribedby }
              : ariaProps,
          )}
        />
        {resize !== 'none' && (
          <ResizeIcon className={styles.resizeIcon(styleVariants)} />
        )}
      </div>
      {hint && (
        <BaseItem
          variant={hasError ? 'error' : 'hint'}
          disabled={disabled || ariaDisabled}
          {...hintProps}
        >
          {hint}
        </BaseItem>
      )}
    </Flex>
  );
});

/**
 * The `TextArea` component can be used as a multi-line plain-text editing control to
 * enter free-form text, such as a comment on a form.
 * The component uses a fallback FormFieldMessages context if it wasn't wrapped in a FormField.
 * @public
 */
export const TextArea = withFormFieldMessageContextProvider<
  TextAreaProps,
  TextAreaInputRef,
  HTMLDivElement
>(TextAreaRoot);

(TextArea as typeof TextArea & { displayName: string }).displayName =
  COMPONENT_NAME;
